import {useRefreshMe} from '../../hooks/session/useRefreshMe';
import {useSession} from '../../hooks/session/SessionProvider';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {CircularProgress, IconButton} from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {signIn} from 'aws-amplify/auth';
import {ConsoleLogger} from 'aws-amplify/utils';
import Lottie from 'lottie-react';
import React, {SyntheticEvent, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate, Link, useLocation} from 'react-router-dom';

import animationSuccess from '../../assets/lottie//animation-success.json';
import {SignupChangePass} from '../../components/signupchangepass/signupchangepass';
import {useValidateEmailMutation} from '../../hooks/auth/mutations/useValidateEmailMutation';
import {useUser} from '../../hooks/session/useSessionSelectors';
import {useDocumentTitle} from '../../useDocumentTitle';
import {HANDLES_API_FAILURE} from '../../utils/mutationMeta';
// import pattern from '../../assets/svg/pattern-lines.svg';

const logger = new ConsoleLogger('Signin');

type SigninState = {
  state: 'idle' | 'loading' | 'error' | 'changepassword' | 'playinganimationstart' | 'playinganimationended';
  email: string;
  password: string;
  error?: {message: string};
  showPassword: boolean;
};

export function Signin() {
  const location = useLocation();
  const [signinState, setSigninState] = useState<SigninState>({
    state: 'idle',
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    email: location.state?.email ?? '',
    password: '',
    error: undefined,
    showPassword: false,
  });

  const {state, email, password, error, showPassword} = signinState;

  const {t} = useTranslation();

  const sessionUser = useUser();
  const refreshMe = useRefreshMe();
  const {refreshSession} = useSession();
  const navigate = useNavigate();
  useDocumentTitle(t('signin.documenttitle'));

  useEffect(() => {
    if (sessionUser === undefined) {
      return;
    }
    if (state === 'playinganimationended') {
      if (sessionUser.lnAccessTokenValid !== true) {
        navigate('/auth/linkedin');
      } else {
        const state: unknown = location.state;
        if (typeof state === 'object' && state !== null && 'from' in state && typeof state.from === 'string') {
          navigate(state.from, {state: {}});
        } else {
          navigate('/');
        }
      }
    }
  }, [sessionUser, state]);

  const onLogin = async () => {
    setSigninState((state) => ({...state, state: 'loading', error: undefined}));
    try {
      const {isSignedIn, nextStep} = await signIn({username: email, password});
      if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setSigninState((state) => ({...state, state: 'changepassword'}));
      } else if (isSignedIn) {
        // `login` est émis par LoginRequiredRoute une fois l'utilisateur identifié : ici il partirait anonyme.
        setSigninState((state) => ({...state, state: 'playinganimationstart'}));
        await refreshSession();
        return refreshMe();
      }
    } catch (error) {
      if (error instanceof Error && error.message !== undefined) {
        const e = error;
        setSigninState((state) => ({...state, state: 'error', error: e}));
      } else {
        setSigninState((state) => ({
          ...state,
          state: 'error',
          // eslint-disable-next-line @typescript-eslint/no-base-to-string
          error: {message: error !== undefined && error !== null ? error.toString() : 'Unknown error'},
        }));
        logger.error(error);
        throw error;
      }
    }
  };

  // Lancée sans attendre après la connexion, en tâche de fond : son échec ne doit pas déranger.
  const validateEmail = useValidateEmailMutation(HANDLES_API_FAILURE);

  const onConfirmChangePass = async () => {
    setSigninState((state) => ({...state, state: 'playinganimationstart'}));
    await refreshSession();
    validateEmail.mutate();
    await refreshMe();
  };

  const handleClickShowPassword = () => {
    setSigninState((prevState) => ({...prevState, showPassword: !prevState.showPassword}));
  };

  const onEmailChange = (email: string) => {
    setSigninState((state) => ({...state, email: email.toLowerCase(), state: 'idle', error: undefined}));
  };

  const onPasswordChange = (pass: string) => {
    setSigninState((state) => ({...state, password: pass, state: 'idle', error: undefined}));
  };

  return (
    <>
      <Box pt={3} px={3}>
        {state === 'changepassword' ? (
          <>
            <Box mb={1}>
              <Typography variant="h4" fontWeight="bold">
                {t('password.haveToChange')}
              </Typography>
            </Box>
          </>
        ) : (
          <>
            <Typography sx={{fontWeight: 700, fontSize: '24px'}}>{t('signin.login.title')}</Typography>
            <Typography color="secondary" sx={{fontWeight: 700, fontSize: '14px'}}>
              {t('signin.login.subtitle')}
            </Typography>
          </>
        )}
      </Box>
      <Box p={3}>
        <Box mt={3}>
          {state === 'changepassword' ? (
            <SignupChangePass email={email} onConfirm={onConfirmChangePass} />
          ) : state === 'playinganimationstart' || state === 'playinganimationended' ? (
            <Lottie
              autoplay
              animationData={animationSuccess}
              initialSegment={[0, 100]}
              loop={false}
              onComplete={() => {
                setSigninState((state) => ({...state, state: 'playinganimationended'}));
              }}
              style={{height: 250}}
            />
          ) : (
            <Box
              component="form"
              role="form"
              onSubmit={(event: SyntheticEvent<HTMLFormElement>) => {
                // Vraie soumission pour que le gestionnaire de mots de passe la détecte, sans rechargement.
                event.preventDefault();
                void onLogin();
              }}
            >
              <Box mb={2}>
                <TextField
                  type="email"
                  autoComplete="username"
                  required
                  fullWidth
                  placeholder={t('form.email.placeholder')}
                  autoFocus
                  value={email}
                  onChange={(event) => {
                    onEmailChange(event.target.value);
                  }}
                  error={error !== undefined}
                />
              </Box>
              <Box mb={2}>
                <TextField
                  required
                  fullWidth
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('form.password.placeholder')}
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => {
                    onPasswordChange(event.target.value);
                  }}
                  error={error !== undefined}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <IconButton
                          aria-label={t('form.password.togglevisibility.arialabel', {})}
                          sx={{p: 0}}
                          onClick={handleClickShowPassword}
                          size="small"
                        >
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      ),
                    },
                  }}
                />
                {error?.message !== undefined && (
                  <Typography variant="body2" fontWeight="bold" color="error">
                    {error.message}
                  </Typography>
                )}

                <Button variant="text" sx={{mt: 1}} component={Link} state={{email}} to="/reset">
                  {t('signin.form.forgotbtn')}
                </Button>
              </Box>
              <Box mt={4} mb={1}>
                <Button variant="contained" color="primary" type="submit" disabled={state === 'loading'} fullWidth>
                  {state === 'loading' ? <CircularProgress size={24} /> : t('signin.login.signbtn')}
                </Button>
              </Box>
              <Typography>
                {t('signin.login.ctasignup')}{' '}
                <Button variant="text" component={Link} state={{email}} to="/signup">
                  {t('signin.login.signupbtn')}
                </Button>
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
}
